import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from '@vastra/ui';
import type { AuthState, User } from '@vastra/types';
import { useToastContext } from './ToastContext';
import { api } from '../services/api';
import {
  isCognitoCustomerConfigured,
  ensureCognitoCustomerConfigured,
  hasCognitoSessionInStorage,
  signOutCustomerCognito,
} from '../lib/cognitoCustomerAuth';

function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decoded = JSON.parse(jsonPayload) as { exp?: number };
    if (typeof decoded.exp === 'number') {
      return Date.now() >= (decoded.exp - 30) * 1000;
    }
    return false;
  } catch {
    return false;
  }
}

interface AuthContextValue extends AuthState {
  login: (email: string, name: string) => void;
  loginWithSession: (user: User, token: string) => void;
  logout: () => void;
  setAuth: (auth: AuthState) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

const INITIAL_AUTH: AuthState = {
  user: null,
  isAuthenticated: false,
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [auth, setAuth] = useLocalStorage<AuthState>('vastra_auth', INITIAL_AUTH);
  const { showToast } = useToastContext();
  const hydratedRef = useRef(false);

  useEffect(() => {
    const base = api.customerApiBase();
    if (!base) return;

    let cancelled = false;
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    (async () => {
      let storedState: AuthState | null = null;
      try {
        const item = typeof window !== 'undefined' ? window.localStorage.getItem('vastra_auth') : null;
        if (item) {
          storedState = JSON.parse(item) as AuthState;
        }
      } catch {
        /* ignore */
      }

      const hasCognito = isCognitoCustomerConfigured() && hasCognitoSessionInStorage();
      const hasStoredLogin = Boolean(storedState?.isAuthenticated && (storedState.token || storedState.user));

      // If user is definitely not logged in, avoid calling Cognito IDP or /api/auth/me
      if (!hasCognito && !hasStoredLogin) {
        return;
      }

      if (hasCognito) {
        await ensureCognitoCustomerConfigured();
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const idTok = session.tokens?.idToken;
          if (idTok) {
            const raw = typeof idTok.toString === 'function' ? idTok.toString() : String(idTok);
            if (!isJwtExpired(raw)) {
              const user = await api.fetchCustomerMe(raw);
              if (!cancelled) setAuth({ user, isAuthenticated: true, token: raw });
              return;
            }
          }
        } catch {
          /* no valid Cognito session */
        }
      }

      const storedToken = storedState?.token;
      if (!storedToken) return;

      // Do not call /api/auth/me with an expired token to avoid 401 error
      if (isJwtExpired(storedToken)) {
        if (!cancelled) setAuth({ user: null, isAuthenticated: false, token: undefined });
        return;
      }

      try {
        const user = await api.fetchCustomerMe(storedToken);
        if (!cancelled) setAuth({ user, isAuthenticated: true, token: storedToken });
      } catch {
        if (!cancelled) setAuth({ user: null, isAuthenticated: false, token: undefined });
      }
    })();

    return () => {
      cancelled = true;
      hydratedRef.current = false;
    };
  }, [setAuth]);

  const login = useCallback(
    (email: string, name: string) => {
      setAuth({
        user: {
          id: 'user-1',
          name,
          email,
          role: 'customer',
          addresses: [],
          createdAt: new Date().toISOString(),
        },
        isAuthenticated: true,
        token: undefined,
      });
      showToast(`Welcome, ${name}!`);
    },
    [setAuth, showToast]
  );

  const loginWithSession = useCallback(
    (user: User, token: string) => {
      setAuth({
        user,
        isAuthenticated: true,
        token,
      });
      showToast(`Welcome, ${user.name}!`);
    },
    [setAuth, showToast]
  );

  const logout = useCallback(async () => {
    if (isCognitoCustomerConfigured()) {
      await signOutCustomerCognito();
    }
    setAuth({ user: null, isAuthenticated: false, token: undefined });
    showToast('Signed out successfully');
  }, [setAuth, showToast]);

  const value: AuthContextValue = {
    ...auth,
    login,
    loginWithSession,
    logout,
    setAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
